import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Navigation from "../../components/Navigation";
import { toast } from "react-toastify";
import { Link, useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Loader";
import Select from "react-select";
import Constants from "../Variables/Constants";
import COUNTRIES from "../Variables/countries";

// Build a quick lookup: countryCode -> country object
const COUNTRY_MAP = Object.fromEntries(COUNTRIES.map((c) => [c.code, c]));
const COUNTRY_CODE_ALIAS_MAP = COUNTRIES.reduce((acc, country) => {
  const code = String(country.code || "").toUpperCase();
  const name = String(country.name || "").toLowerCase();
  if (code) acc[code.toLowerCase()] = code;
  if (name) acc[name] = code;
  return acc;
}, {});

const resolveCountryCode = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return COUNTRY_CODE_ALIAS_MAP[normalized.toLowerCase()] || normalized.toUpperCase();
};

const resolveStateCode = (countryCode, value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";

  const states = COUNTRY_MAP[countryCode]?.states || [];
  const matchedState = states.find(
    (state) =>
      state.code.toLowerCase() === normalized.toLowerCase() ||
      state.name.toLowerCase() === normalized.toLowerCase()
  );

  return matchedState?.code || normalized.toUpperCase();
};

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 58,
    borderRadius: 16,
    borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
    boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13,110,253,.25)" : "none",
    fontSize: 16,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "10px 14px",
  }),
  input: (provided) => ({
    ...provided,
    fontSize: 16,
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: 16,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#6c757d",
    fontSize: 16,
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 40,
  }),
};

const AddAddress = () => {
  const { id } = useParams();
  const TOKEN = localStorage.getItem("token");
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [upsValidation, setUpsValidation] = useState(null);
  const [upsLoading, setUpsLoading] = useState(false);

  const [formData, setFormData] = useState({
    address_name: "", 
    country: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    is_default: "no", // Default value
  });

  // Fetch data if id exists
  useEffect(() => {
    try {
      if (id) {
        const fetchAddress = async () => {
          setLoader(true);
          const response = await axios.get(
            `${Constants.getUrls.getUserAddress}/${id}`,
            {
              headers: {
                Authorization: TOKEN,
              },
            }
          );

          const fetchedData = response.data.data || {};
          const resolvedCountryCode = resolveCountryCode(fetchedData.country);
          setFormData({
            ...fetchedData,
            country: resolvedCountryCode,
            state: resolveStateCode(resolvedCountryCode, fetchedData.state),
          });
          setLoader(false);
          // Populate form with fetched data
        };

        fetchAddress();
      }
    } catch (error) {
      try {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(error.message);
        const { status, message } = error.response.data;
        if (status === 401 && message === "Unauthorized!") {
          localStorage.removeItem("token");
          window.location.href = "/";
        }
      } catch (error) {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(error.message);
      }
    }
  }, [id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setUpsValidation(null);
  };

  const selectedCountryStates = COUNTRY_MAP[formData.country]?.states || [];
  const countryOptions = COUNTRIES.map((country) => ({
    value: country.code,
    label: `${country.name} (${country.code})`,
  }));
  const stateOptions = selectedCountryStates.map((state) => ({
    value: state.code,
    label: `${state.name} (${state.code})`,
  }));

  const getCountryNameByCode = (countryCode) =>
    COUNTRY_MAP[countryCode]?.name || String(countryCode || "").trim();

  const getStateNameByCode = (countryCode, stateCode) => {
    const stateItem = (COUNTRY_MAP[countryCode]?.states || []).find(
      (state) => state.code === stateCode
    );
    return stateItem?.name || String(stateCode || "").trim();
  };

  const getUpsValidationStatus = (data) => {
    const response = data?.XAVResponse || data?.data?.XAVResponse;
    if (response?.ValidAddressIndicator !== undefined) return "valid";
    if (response?.AmbiguousAddressIndicator !== undefined) return "ambiguous";
    if (response?.NoCandidatesIndicator !== undefined) return "no_candidates";
    return "unknown";
  };

  const validateUpsAddress = useCallback(async ({ showToast = false, signal } = {}) => {
    let wasCanceled = false;
    try {
      setUpsLoading(true);
      setUpsValidation({ status: "checking" });

      const response = await axios.post(
        Constants.postUrls.validateUpsAddress,
        {
          address: {
            name: formData.address_name,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            postal_code: formData.postal_code,
            country: formData.country || "US",
          },
        },
        {
          headers: {
            Authorization: TOKEN,
          },
          signal,
        }
      );

      const validationStatus = getUpsValidationStatus(response.data.data);
      setUpsValidation({
        status: validationStatus,
        environment: response.data.environment,
        candidates: response.data.data?.XAVResponse?.Candidate || [],
      });

      if (!showToast) {
        return;
      }

      if (validationStatus === "valid") {
        toast.success("UPS validated this address.");
      } else if (validationStatus === "ambiguous") {
        toast.info("UPS found multiple possible address matches.");
      } else if (validationStatus === "no_candidates") {
        toast.error("UPS could not find a matching address.");
      } else {
        toast.info("UPS returned a validation response.");
      }
    } catch (error) {
      wasCanceled = error?.name === "CanceledError" || error?.code === "ERR_CANCELED";
      if (wasCanceled) {
        return;
      }

      setUpsValidation({
        status: "error",
        message: error?.response?.data?.message || error.message,
      });

      if (!showToast) {
        return;
      }

      toast.clearWaitingQueue();
      toast.dismiss();
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      if (!wasCanceled) {
        setUpsLoading(false);
      }
    }
  }, [TOKEN, formData.address, formData.address_name, formData.city, formData.country, formData.postal_code, formData.state]);

  const handleValidateUpsAddress = () => {
    validateUpsAddress({ showToast: true });
  };

  useEffect(() => {
    const requiredAddressFields = [
      formData.country,
      formData.address,
      formData.city,
      formData.postal_code,
    ];
    const isReadyForValidation = requiredAddressFields.every(
      (value) => String(value || "").trim().length > 0
    );

    if (!isReadyForValidation) {
      setUpsValidation(null);
      setUpsLoading(false);
      return;
    }

    const controller = new AbortController();
    const debounceId = setTimeout(() => {
      validateUpsAddress({ signal: controller.signal });
    }, 800);

    return () => {
      clearTimeout(debounceId);
      controller.abort();
    };
  }, [
    formData.address,
    formData.city,
    formData.country,
    formData.postal_code,
    formData.state,
    validateUpsAddress,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.country) {
        toast.error("Please select a country.");
        return;
      }

      const countryCode = String(formData.country || "").trim().toUpperCase();
      const resolvedStateCode = resolveStateCode(countryCode, formData.state);
      const payload = {
        ...formData,
        country: getCountryNameByCode(countryCode),
        country_code: countryCode,
        state: getStateNameByCode(countryCode, resolvedStateCode),
        state_code: resolvedStateCode,
      };

      if (id) {
        // Update existing address
        await axios.put(`${Constants.putUrls.updateUserAddress}/${id}`, payload, {
          headers: {
            Authorization: TOKEN,
          },
        });
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.success("Address updated successfully!");
      } else {
        // Create new address
        await axios.post(`${Constants.postUrls.addAddress}`, payload, {
          headers: {
            Authorization: TOKEN,
          },
        });
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.success("Address added successfully!");
      }

      // Reset form after submission
      setFormData({
        address_name: "",
        country: "",
        address: "",
        city: "",
        state: "",
        postal_code: "",
        is_default: "no",
      });
      navigate("/address");
    } catch (error) {
      try {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(error.message);
        const { status, message } = error.response.data;
        if (status === 401 && message === "Unauthorized!") {
          localStorage.removeItem("token");
          window.location.href = "/";
        }
      } catch (error) {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(error.message);
      }
    }
  };

  return (
    <div className="container mt-4">
      {loader && <Loader />}
      <Navigation id={id} />
      <div className="mx-auto addAddress p-4">
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <input
              type="text"
              name="address_name"
              value={formData.address_name}
              onChange={handleChange}
              className="form-control p-4 rounded-4"
              placeholder="Enter Address Name"
              required
            />
          </div>

          <div className="mb-3">
            <Select
              options={countryOptions}
              value={countryOptions.find((option) => option.value === formData.country) || null}
              onChange={(option) => {
                setFormData({ ...formData, country: option?.value || "", state: "" });
                setUpsValidation(null);
              }}
              placeholder="Select Country / Region"
              isSearchable
              styles={selectStyles}
            />
          </div>

          <div className="mb-3">
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="form-control p-4 rounded-4"
              placeholder="Address"
              required
            />
          </div>

          <div className="row mb-4">
            <div className="col-md-6 mb-3">
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="form-control p-4 rounded-4"
                placeholder="City"
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              {COUNTRY_MAP[formData.country]?.states?.length > 0 ? (
                <Select
                  options={stateOptions}
                  value={stateOptions.find((option) => option.value === (formData.state || "")) || null}
                  onChange={(option) => {
                    setFormData({ ...formData, state: option?.value || "" });
                    setUpsValidation(null);
                  }}
                  placeholder="Select State / Province"
                  isSearchable
                  styles={selectStyles}
                />
              ) : (
                <input
                  type="text"
                  name="state"
                  value={formData.state || ""}
                  onChange={handleChange}
                  className="form-control p-4 rounded-4"
                  placeholder="State / Province / Region"
                />
              )}
            </div>
            <div className="col-md-6 mb-3">
              <input
                type="number"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                className="form-control p-4 rounded-4"
                placeholder="Postal Code"
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <button
              type="button"
              className="btn border-0 bg-light-gray me-3"
              disabled={upsLoading}
              onClick={handleValidateUpsAddress}
            >
              {upsLoading ? "Validating..." : "Validate with UPS"}
            </button>
            {upsValidation && (
              <span className="fs-14">
                {upsValidation.status === "checking"
                  ? "UPS: checking address..."
                  : upsValidation.status === "error"
                    ? `UPS: ${upsValidation.message}`
                    : `UPS ${upsValidation.environment}: ${upsValidation.status.replace("_", " ")}`}
              </span>
            )}
          </div>

          <div className="d-flex gap-4 mt-4 justify-content-between">
            <Link to="/address" className="w-50">
              <button
                type="button"
                className="btn border-0 w-100 bg-light-gray"
              >
                Cancel
              </button>
            </Link>
            <button type="submit" className="btn w-50 btn-warning text-white">
              {id ? "Update" : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAddress;
